import {Injectable} from '@angular/core';
import {
    AccountCardResponse,
    AccountResponse,
    Faq,
    IncidentTransaction,
    Participant,
    ParticipantInfo,
    PaymentPointResponse,
    PhoneNumberResponse,
    ReferenceBookResponse,
    WalletResponse,
} from '@npck/af-frontend-api';
import {
    formatDateTimeFromServerToUi,
    formatFromServerToUi,
    LocalizationService,
    NumberTriplesFormatPipe,
    TiynTengePipe,
} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {findValueInDictionary} from './dictionary-holder.service';
import {formatPhone} from './phone-utils';

const PARTICIPANT_INFO_FIELD_NAMES: (keyof ParticipantInfo | string)[] = ['origin_date', 'gender'];
const REQUISITES_FIELD_NAMES = [
    'wallet',
    'paymentPoint',
    'account',
    'foreignAccount',
    'card_mask',
];
const ACCOUNT_FIELD_NAMES: (keyof AccountResponse)[] = ['iban', 'issuerBin', 'status', 'balance'];
const FOREIGN_ACCOUNT_FIELD_NAMES: (keyof AccountResponse)[] = ['iban', 'issuerBin'];
const CARD_MASK_FIELD_NAMES: (keyof AccountCardResponse)[] = ['card_number', 'issuer_bin'];
const WALLET_FIELD_NAMES: (keyof WalletResponse)[] = ['number', 'issuer_bin'];
const PAYMENT_POINT_FIELD_NAMES: (keyof PaymentPointResponse)[] = ['number', 'issuer_bin', 'address', 'street', 'house_number'];
export const PARTICIPANT_FIELD_NAMES: (keyof Participant | string)[] =
    ['incident_role', 'type', 'info', 'identification_number', 'phone_number', 'requisites'];
const MIN_VALID_PHONE_NUMBER_LENGTH = 11;

export const PARTICIPANT_NESTED_FIELD = {
    info: PARTICIPANT_INFO_FIELD_NAMES,
    requisites: REQUISITES_FIELD_NAMES,
    account: ACCOUNT_FIELD_NAMES,
    foreignAccount: FOREIGN_ACCOUNT_FIELD_NAMES,
    card_mask: CARD_MASK_FIELD_NAMES,
    wallet: WALLET_FIELD_NAMES,
    paymentPoint: PAYMENT_POINT_FIELD_NAMES,
};

@Injectable({
    providedIn: 'root',
})
export class FieldBuilderService {
    private tyinTengePipe = new TiynTengePipe();
    private numberTrippletsPipe = new NumberTriplesFormatPipe();

    constructor(private localizationService: LocalizationService) {
    }

    buildFields(fieldBuilderData: FieldBuilderData): FieldInfo[] {
        return fieldBuilderData.fieldNames.map(fieldName => {
            let modifiedFieldName: string;
            let value = (fieldBuilderData.data as any)[fieldName];
            let expandableValue: FieldInfo[];
            if (fieldBuilderData.customValueMapper?.[fieldName]) {
                value = fieldBuilderData.customValueMapper[fieldName]?.(value);
            } else if (fieldName.toLowerCase().includes('amount') && value !== null && value !== undefined) {
                value = this.tyinTengePipe.transform(value);
                value = this.numberTrippletsPipe.transform(value);
                value = `${value}${(fieldBuilderData.data as any).currency ? ` ${(fieldBuilderData.data as any).currency}` : ''}`;
            } else if (['date', 'created_date'].includes(fieldName)) {
                value = value && formatDateTimeFromServerToUi(dayjs(value));
            } else if (fieldName === 'info') {
                const participantInfo = (value as ParticipantInfo);
                value = [participantInfo.surname, participantInfo.name, participantInfo.patronymic]
                    .filter(fullNameValue => !!fullNameValue).join(' ');
                let infoFieldNames = fieldBuilderData.nestedFieldNames[fieldName];
                if (fieldBuilderData.type === 'ORGANIZATION') {
                    modifiedFieldName = 'organization_name';
                    infoFieldNames = ['origin_date'];
                }
                expandableValue = this.buildFields({...fieldBuilderData, fieldNames: infoFieldNames});
                (fieldBuilderData.data as Participant).country &&
                expandableValue.push({fieldName: 'country', fieldValue: (fieldBuilderData.data as Participant).country || '-'});
            } else if (fieldName === 'requisites') {
                expandableValue = this.buildFields({...fieldBuilderData, fieldNames: fieldBuilderData.nestedFieldNames[fieldName]});
            } else if (fieldName === 'phone_number') {
                const phoneInfo = (value as PhoneNumberResponse);
                value = phoneInfo.number?.length >= MIN_VALID_PHONE_NUMBER_LENGTH ? formatPhone(phoneInfo.number) : '-';
            } else if (fieldName === 'card_mask') {
                expandableValue = this.buildFields({
                    ...fieldBuilderData,
                    data: value as AccountCardResponse,
                    fieldNames: fieldBuilderData.nestedFieldNames[fieldName],
                });
            } else if (fieldName === 'account' || fieldName === 'foreignAccount') {
                expandableValue = this.buildFields({
                    ...fieldBuilderData,
                    data: value as AccountResponse,
                    fieldNames: fieldBuilderData.nestedFieldNames[fieldName],
                });
            } else if (fieldName === 'wallet') {
                expandableValue = this.buildFields({
                    ...fieldBuilderData,
                    data: value as WalletResponse,
                    fieldNames: fieldBuilderData.nestedFieldNames[fieldName],
                });
            } else if (fieldName === 'paymentPoint') {
                expandableValue = this.buildFields({
                    ...fieldBuilderData,
                    data: value as PaymentPointResponse,
                    fieldNames: fieldBuilderData.nestedFieldNames[fieldName],
                });
            } else if (fieldName === 'incident_role') {
                value = findValueInDictionary(fieldBuilderData.dictionaries.roles, value.toLowerCase(), 'name_ru');
            } else if (fieldName === 'issuer_bin' || fieldName === 'issuerBin') {
                const isForeignAccount = (fieldBuilderData.data as AccountResponse)?.isForeignAccount;
                value = value
                    ? (isForeignAccount ? value : findValueInDictionary(fieldBuilderData.dictionaries.bins, value.toLowerCase(), 'name_ru'))
                    : '-';
            } else if (fieldName === 'type' && fieldBuilderData.context === 'participant') {
                value = this.localizationService.getLocalizedKey(`i18n_af_subjectType#${value.toUpperCase()}`);
            } else if (fieldName === 'origin_date') {
                modifiedFieldName = fieldBuilderData.type === 'ORGANIZATION' ? 'registration_date' : 'birth_date';
                const originDate = (fieldBuilderData.data as Participant)?.info?.origin_date;
                value = originDate && formatFromServerToUi(dayjs(originDate));
            } else if (fieldName == 'gender') {
                value = (fieldBuilderData.data as Participant)?.info?.gender;
            } else if (
                fieldName === 'identification_number' &&
                fieldBuilderData.context === 'participant' &&
                fieldBuilderData.type === 'ORGANIZATION'
            ) {
                modifiedFieldName = 'bin';
            } else if (fieldName === 'status' && fieldBuilderData.context === 'participant') {
                value = findValueInDictionary(fieldBuilderData.dictionaries?.accountStatuses ?? [], value, 'name_ru') ?? value;
            } else if (fieldName === 'balance' && value !== null && value !== undefined) {
                value = this.tyinTengePipe.transform(value);
                value = this.numberTrippletsPipe.transform(value);
                value = `${value}${(fieldBuilderData.data as AccountResponse).currency
                    ? ` ${(fieldBuilderData.data as AccountResponse).currency}`
                    : ''}`;
            } else if (fieldName === 'type' && fieldBuilderData.context === 'refund') {
                value = findValueInDictionary(fieldBuilderData.dictionaries.refundTypes, value, 'name_ru');
            } else if (fieldName === 'grounds') {
                value = findValueInDictionary(fieldBuilderData.dictionaries.grounds, value, 'name_ru');
            } else if (fieldName === 'prosecutorOfficeBin') {
                value = findValueInDictionary(fieldBuilderData.dictionaries.bins, value, 'name_ru');
            } else if (fieldName.toLowerCase().includes('currency')) {
                value = findValueInDictionary(fieldBuilderData.dictionaries.currencies, value, 'code1');
            } else if (fieldName.toLowerCase().includes('date')) {
                value = value && formatFromServerToUi(dayjs(value));
            } else if (fieldName === 'transactionCategory') {
                const transactionType = findValueInDictionary(
                    fieldBuilderData.dictionaries.transactionTypes,
                    (fieldBuilderData.data as any).transactionType,
                    'code1',
                );
                value = findValueInDictionary(fieldBuilderData.dictionaries.transactionCategories, transactionType, 'name_ru');
            } else if (fieldName === 'faqCategory') {
                value = findValueInDictionary(
                    fieldBuilderData.dictionaries.faqCategories,
                    (fieldBuilderData.data as Faq).category,
                    'name_ru',
                );
            } else if (fieldName === 'transactionType') {
                value = findValueInDictionary(
                    fieldBuilderData.dictionaries.transactionTypes,
                    (fieldBuilderData.data as any).transactionType,
                    'name_ru',
                );
            } else if (fieldName === 'transactionIds') {
                expandableValue = (value as string[]).map((id, index) => <FieldInfo>{
                    fieldName: '-',
                    fieldValue: this.localizationService.getLocalizedKey('i18n_af_labelIncidentTransactionTitle', {index: `${index + 1}`}),
                    click: () => fieldBuilderData.fieldClick[fieldName]?.(id),
                });
                value = '-';
            } else if (fieldName === 'clientConfirmed') {
                value = this.localizationService.getLocalizedKey(`i18n_af_clientConfirmed#${value}`);
            } else if (fieldName === 'confirmationMethod') {
                value = findValueInDictionary(fieldBuilderData.dictionaries.confirmationMethods, value, 'name_ru');
            } else if (fieldName === 'clientLegitimacyConfirmation') {
                value = this.localizationService.getLocalizedKey(`i18n_af_clientLegitimacyConfirmation#${value}`);
            }
            return {
                fieldName: modifiedFieldName || fieldName,
                hasClickableItems: ['transactionIds'].includes(fieldName),
                isExpandable: ['info', 'account', 'wallet', 'paymentPoint', 'requisites'].includes(fieldName),
                fieldValue: value || '-',
                expandableValue: expandableValue,
            };
        });
    }
}

export interface FieldInfo {
    fieldName: string;
    fieldValue: string;
    isExpandable?: boolean;
    isExpanded?: boolean;
    hasClickableItems?: boolean;
    click?: () => void;
    expandableValue?: FieldInfo[];
}

export interface FieldBuilderData {
    fieldNames: FieldNames,
    data: FieldData,
    dictionaries?: Dictionaries,
    nestedFieldNames?: Record<string, FieldNames>,
    fieldClick?: Record<string, (id: string) => void>
    context?: 'participant' | 'refund',
    type?: string,
    customValueMapper?: Record<string, (value: string) => string>
}

export interface Dictionaries {
    bins?: ReferenceBookResponse[];
    accountStatuses?: ReferenceBookResponse[];
    roles?: ReferenceBookResponse[];
    refundTypes?: ReferenceBookResponse[];
    grounds?: ReferenceBookResponse[];
    currencies?: ReferenceBookResponse[];
    transactionCategories?: ReferenceBookResponse[];
    transactionTypes?: ReferenceBookResponse[];
    faqCategories?: ReferenceBookResponse[];
    contactTypes?: ReferenceBookResponse[];
    timeframes?: ReferenceBookResponse[];
    reasons?: ReferenceBookResponse[];
    actions?: ReferenceBookResponse[];
    callContactTypes?: ReferenceBookResponse[];
    messengerContactSubtypes?: ReferenceBookResponse[];
    emailContactTypes?: ReferenceBookResponse[];
    socialContactTypes?: ReferenceBookResponse[];
    internetContactTypes?: ReferenceBookResponse[];
    confirmationMethods?: ReferenceBookResponse[];
}

export declare type FieldNames =
    (keyof IncidentTransaction)[]
    | (keyof Participant)[]
    | (keyof PhoneNumberResponse)[]
    | (keyof AccountResponse)[]
    | (keyof ParticipantInfo)[]
    | (keyof WalletResponse) []
    | (keyof PaymentPointResponse) []
    | (keyof Faq) []
    | string[];

export declare type FieldData =
    | IncidentTransaction
    | Participant
    | PhoneNumberResponse
    | AccountResponse
    | ParticipantInfo
    | WalletResponse
    | PaymentPointResponse
    | Faq;
